import { Controller } from "@hotwired/stimulus"

// Polls for updates to the conversation list (new conversations, unread counts, etc.)
export default class extends Controller {
  static values = {
    interval: { type: Number, default: 10000 }, // 10 seconds
    lastUpdate: String // ISO timestamp of last check
  }

  connect() {
    this.isDisconnected = false
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.lastUpdateValue = new Date().toISOString()
    this.startPolling()
    
    // Pause polling when tab is not visible
    document.addEventListener('visibilitychange', this.boundHandleVisibilityChange)
  }

  disconnect() {
    this.isDisconnected = true  // Flag to abort any in-flight requests
    this.stopPolling()
    document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange)
  }

  startPolling() {
    if (this.pollingTimer || this.isDisconnected) return
    
    this.pollingTimer = setInterval(() => {
      if (!this.isDisconnected) {
        this.checkForUpdates()
      }
    }, this.intervalValue)
  }

  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
    }
  }

  handleVisibilityChange() {
    if (this.isDisconnected) return
    
    if (document.hidden) {
      this.stopPolling()
    } else {
      this.checkForUpdates() // Check immediately when tab becomes visible
      this.startPolling()
    }
  }

  async checkForUpdates() {
    if (this.isDisconnected) return  // Don't fetch if disconnected
    
    try {
      const response = await fetch(
        `/conversations/poll_list?since=${encodeURIComponent(this.lastUpdateValue)}`,
        {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      )

      if (!response.ok || this.isDisconnected) return  // Check again after async

      const data = await response.json()
      
      if (this.isDisconnected) return  // Check again after parsing
      
      if (data.has_updates && data.html) {
        this.updateConversationList(data.html)
      }
      
      // Always update the unread badge in sidebar
      this.updateUnreadBadge(data.unread_count)
      
      this.lastUpdateValue = data.checked_at
    } catch (error) {
      if (!this.isDisconnected) {
        console.error('Error polling for conversation updates:', error)
      }
    }
  }

  updateConversationList(html) {
    if (this.isDisconnected) return
    
    const listContainer = this.element.querySelector('.conversations-list') || this.element

    if (listContainer) {
      // Preserve the currently active conversation
      const activeCard = listContainer.querySelector('.conversation-card-active')
      const activeId = activeCard?.closest('a')?.href?.match(/conversations\/(\d+)/)?.[1]
      
      listContainer.innerHTML = html
      
      // Re-apply active state if needed
      if (activeId) {
        const newActiveCard = listContainer.querySelector(`a[href*="conversations/${activeId}"] .conversation-card, a[href$="conversations/${activeId}"]`)
        if (newActiveCard) {
          const card = newActiveCard.classList.contains('conversation-card') ? newActiveCard : newActiveCard.querySelector('.conversation-card')
          if (card) card.classList.add('conversation-card-active')
        }
      }
    }
  }

  updateUnreadBadge(count) {
    const badges = [
      document.getElementById('messages-unread-badge'),
      document.getElementById('mobile-messages-unread-badge')
    ]

    badges.forEach(badge => {
      if (!badge) return
      if (count > 0) {
        badge.textContent = count > 99 ? '+99' : count
        badge.classList.remove('d-none')
      } else {
        badge.classList.add('d-none')
      }
    })
  }
}
